import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowRight, MessageCircle, Sparkles, ScanLine, PieChart, ChevronRight } from 'lucide-react';
import Seo from '../components/Seo';
import LandingHeader from '../components/LandingHeader';
import RelatedLinks from '../components/RelatedLinks';
import { WHATSAPP_LINK } from '../utils/constants';

const STEPS = [
  { icon: MessageCircle, title: 'You send it in plain language', desc: 'Text “got paid $900 by Acme” or “spent $45 on fuel”, or snap a receipt — no forms, no categories to pick.' },
  { icon: Sparkles, title: 'AI reads and categorizes it', desc: 'MoneyFeed’s AI extracts the amount, vendor, date and tax, then files it to the correct tax line automatically.' },
  { icon: PieChart, title: 'Your books stay tax-ready', desc: 'A live profit & loss and category summary update instantly, ready to export as PDF, CSV or Excel.' },
];

const FAQS = [
  { q: 'What is AI bookkeeping?', a: 'AI bookkeeping is a way of keeping your books where software automatically reads your income, expenses and receipts and categorizes each one for you, instead of you typing every transaction into accounting software or a spreadsheet. MoneyFeed does this through WhatsApp: you send a message or a receipt photo, and the AI records and categorizes it.' },
  { q: 'How does AI bookkeeping work?', a: 'You send a transaction in plain language (or a receipt image). The AI extracts the key details — amount, vendor, date, tax — and maps it to the right category (for example an IRS Schedule C line in the US). Your profit & loss and tax summary update automatically.' },
  { q: 'Can AI bookkeeping replace spreadsheets?', a: 'For most freelancers and small businesses, yes — it removes the manual data entry, categorization and formulas that spreadsheets require. You still get clean exports (CSV/Excel/PDF) if you or your accountant want a spreadsheet at tax time.' },
  { q: 'Is AI bookkeeping accurate?', a: 'The AI does the first pass and is right the large majority of the time; you can review and correct any entry, and every receipt image is archived so you keep a full audit trail. MoneyFeed provides estimates and tax-ready records and does not replace a professional accountant.' },
];

export default function AiBookkeeping() {
  const jsonLd = [
    { '@context': 'https://schema.org', '@type': 'WebPage', name: 'AI Bookkeeping', url: 'https://moneyfeed.io/ai-bookkeeping', description: 'What AI bookkeeping is and how it works: software that reads and categorizes your income, expenses and receipts automatically. MoneyFeed does it through WhatsApp.' },
    { '@context': 'https://schema.org', '@type': 'FAQPage', mainEntity: FAQS.map((f) => ({ '@type': 'Question', name: f.q, acceptedAnswer: { '@type': 'Answer', text: f.a } })) },
  ];
  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F8FAFC] via-[#ECFDF5] to-[#D1FAE5]" data-testid="ai-bookkeeping-page">
      <Seo
        title="Bookkeeping on WhatsApp for Freelancers & Small Businesses | MoneyFeed"
        description="AI bookkeeping reads and categorizes your income, expenses and receipts automatically — no spreadsheets. See how MoneyFeed does it through WhatsApp."
        path="/ai-bookkeeping"
        jsonLd={jsonLd}
      />
      <LandingHeader />

      <nav className="max-w-5xl mx-auto px-4 sm:px-8 pt-6 text-xs text-gray-500 flex items-center gap-1" aria-label="Breadcrumb" data-testid="ai-bookkeeping-breadcrumb">
        <Link to="/" className="hover:text-[#064E3B]">Home</Link>
        <ChevronRight className="h-3 w-3" /> <span className="text-[#064E3B] font-semibold">AI Bookkeeping</span>
      </nav>

      <section className="max-w-3xl mx-auto px-4 sm:px-8 pt-8 pb-6 text-center">
        <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-[#84CC16]/15 rounded-full mb-5">
          <Sparkles className="h-4 w-4 text-[#65A30D]" />
          <span className="text-xs font-bold text-[#65A30D] uppercase tracking-wider">AI Bookkeeping</span>
        </div>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#064E3B] leading-tight mb-5">Your bookkeeping app, now on WhatsApp.</h1>
        <div className="bg-white rounded-2xl shadow-md p-6 text-left max-w-2xl mx-auto" data-testid="ai-bookkeeping-answer">
          <p className="text-base sm:text-lg text-gray-700">
            <strong>AI bookkeeping</strong> is a way of keeping your books where software automatically reads your income, expenses and receipts and categorizes each one — instead of you entering every transaction by hand. <strong>MoneyFeed is a WhatsApp tool that works with your bookkeeping app for freelancers and small businesses:</strong> you send a message or a receipt photo, and the AI records, categorizes and keeps it tax-ready.
          </p>
        </div>
        <div className="flex flex-col sm:flex-row gap-3 justify-center mt-6">
          <Link to="/auth"><Button className="w-full sm:w-auto bg-[#84CC16] hover:bg-[#65A30D] text-black font-bold rounded-full px-7 py-6 text-base" data-testid="ai-bookkeeping-get-started">Start free <ArrowRight className="h-4 w-4 ml-1" /></Button></Link>
          <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer"><Button variant="outline" className="w-full sm:w-auto border-[#25D366] text-[#128C7E] hover:bg-[#25D366]/10 font-bold rounded-full px-7 py-6 text-base" data-testid="ai-bookkeeping-whatsapp"><MessageCircle className="h-4 w-4 mr-1" /> Try it on WhatsApp</Button></a>
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-4 sm:px-8 py-10">
        <h2 className="text-2xl font-bold text-[#064E3B] mb-8 text-center">How AI bookkeeping works in MoneyFeed</h2>
        <div className="grid sm:grid-cols-3 gap-6">
          {STEPS.map((s, i) => (
            <div key={s.title} className="bg-white rounded-2xl shadow-md p-6" data-testid={`ai-bookkeeping-step-${i}`}>
              <div className="w-11 h-11 rounded-xl bg-[#064E3B] flex items-center justify-center mb-4"><s.icon className="h-5 w-5 text-[#84CC16]" /></div>
              <h3 className="font-bold text-[#064E3B] mb-1 text-base">{s.title}</h3>
              <p className="text-gray-600 text-sm">{s.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="max-w-3xl mx-auto px-4 sm:px-8 py-6">
        <div className="bg-white rounded-2xl shadow-md p-7">
          <h2 className="text-xl font-bold text-[#064E3B] mb-3">Who it’s for</h2>
          <p className="text-gray-700 text-sm mb-4">Freelancers, 1099 and gig workers, sole traders, and small business owners who want tax-ready books without learning traditional accounting software. Explore it by workflow:</p>
          <div className="flex flex-wrap gap-2">
            {[['WhatsApp bookkeeping', '/whatsapp-bookkeeping'], ['Expense tracking', '/expense-tracking'], ['Receipt scanning', '/receipt-to-excel'], ['Mileage tracking', '/mileage-tracking'], ['1099 bookkeeping', '/1099-bookkeeping'], ['Freelancer bookkeeping', '/freelancer-bookkeeping']].map(([l, to]) => (
              <Link key={to} to={to} className="text-xs font-semibold text-[#064E3B] px-3 py-1.5 rounded-full bg-[#F0FDF4] hover:bg-[#84CC16]/20 transition-colors" data-testid={`ai-bookkeeping-link-${to}`}>{l}</Link>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-3xl mx-auto px-4 sm:px-8 py-12">
        <h2 className="text-3xl font-bold text-[#064E3B] text-center mb-10">AI bookkeeping FAQ</h2>
        <div className="space-y-4">
          {FAQS.map((f, i) => (
            <div key={i} className="bg-white rounded-2xl shadow-sm p-6" data-testid={`ai-bookkeeping-faq-${i}`}>
              <h3 className="font-bold text-[#064E3B] mb-2">{f.q}</h3>
              <p className="text-gray-600 text-sm">{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-4 sm:px-8 py-8">
        <div className="bg-[#064E3B] rounded-3xl p-10 text-center">
          <h2 className="text-3xl font-bold text-white mb-3">Let AI keep your books</h2>
          <p className="text-[#A7F3D0] mb-6 max-w-xl mx-auto">Start free — text your first expense and watch it get categorized.</p>
          <Link to="/demo"><Button className="bg-[#84CC16] hover:bg-[#65A30D] text-black font-bold rounded-full px-8 py-6 text-base" data-testid="ai-bookkeeping-cta-demo">See the live demo <ArrowRight className="h-4 w-4 ml-1" /></Button></Link>
        </div>
      </section>

      <RelatedLinks exclude={['/ai-bookkeeping']} />
      <footer className="max-w-6xl mx-auto px-4 sm:px-8 py-8 text-center">
        <div className="flex flex-wrap justify-center gap-6 text-sm text-gray-500">
          <Link to="/" className="hover:text-[#064E3B]">Home</Link>
          <Link to="/whatsapp-accounting" className="hover:text-[#064E3B]">WhatsApp Accounting</Link>
          <Link to="/whatsapp-bookkeeping" className="hover:text-[#064E3B]">WhatsApp Bookkeeping</Link>
          <Link to="/features" className="hover:text-[#064E3B]">Features</Link>
        </div>
      </footer>
    </div>
  );
}
